import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const CHAIN = ['Where you are', 'Who you are', 'What you can do', 'What you want',
  'What fits', 'What is missing', 'Your options', 'Who pays', 'Your path', 'Action'];

const JOURNEY = [
  ['Self-Discovery', 'Interests, traits, values and the environments you thrive in'],
  ['The Life I Want', 'The cost of that life, and the salary it requires'],
  ['Exploration', 'Careers, colleges, trades, military, entrepreneurship'],
  ['Intelligence', 'Fit, skills, gaps and where the work is heading'],
  ['Decision & Design', 'Options compared side by side, futures priced'],
  ['Funding', 'Grants, scholarships, benefits and what you can afford'],
  ['Career Launch', 'Readiness, positioning, proof of skill, mentors'],
  ['Execution', 'A dated plan that rebuilds itself when life changes'],
];



export default function HowItWorks() {
  return (
    <section id="how" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto">
        {/* One question */}
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-300">What it is</span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mt-4 leading-tight">
            One question. Every transition.
          </h2>
          <p className="text-lg text-slate-300/90 mt-5 leading-relaxed">
            A sixteen-year-old choosing courses, a senior heading into a first job, a manager whose role was
            eliminated, a soldier separating after twelve years, a parent returning after eight years out.
            The circumstances look nothing alike. The question underneath is identical:
          </p>
          <p className="font-outfit text-xl sm:text-2xl text-white mt-6">
            Where are you now, where do you want to go, and what is the smartest pathway to get there?
          </p>
          <p className="text-base text-slate-300/85 mt-5">
            So we do not ask how old you are. We ask what is happening in your life right now, as many
            answers as apply, and switch on the intelligence that decision actually needs.
          </p>
        </div>

        {/* The chain */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-3 mt-12" data-testid="intelligence-chain">
          {CHAIN.map((step, i) => (
            <motion.span key={step} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="inline-flex items-center gap-2">
              <span className="rounded-full border border-cyan-300/25 bg-cyan-300/[0.07] px-3.5 py-1.5 text-sm text-slate-200">
                {step}
              </span>
              {i < CHAIN.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-cyan-300/50" />}
            </motion.span>
          ))}
        </div>

        {/* The journey */}
        <div className="mt-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-violet-300">The journey</span>
          <h3 className="font-outfit text-2xl sm:text-3xl font-semibold text-white mt-4">
            Eight steps, each one feeding the next
          </h3>
          <p className="text-base text-slate-300/85 mt-4 max-w-3xl">
            Nothing here is a standalone quiz. Your assessment shapes your lifestyle design, your lifestyle
            sets the income floor, the floor filters every career we recommend, and all of it lands in one report.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {JOURNEY.map(([title, body], i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className={`glass-tint ${['glass-cyan', 'glass-violet', 'glass-emerald', 'glass-amber'][i % 4]} p-5`}>
                <span className="font-outfit text-2xl font-bold text-white/25">{String(i + 1).padStart(2, '0')}</span>
                <p className="font-outfit text-base font-semibold text-white mt-2">{title}</p>
                <p className="text-sm text-slate-300/80 mt-1.5 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
